import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ClipboardCheck, Clock, Pencil, Plus, Receipt, Search, Trash2, Wrench } from "lucide-react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { ServiceDialog } from "@/components/maintenance/ServiceDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useTraccarSession } from "@/hooks/useTraccarSession";
import {
  createMaintenance,
  deleteMaintenance,
  fetchDevices,
  fetchMaintenances,
  fetchPositions,
  linkMaintenanceToDevice,
  unlinkMaintenanceFromDevice,
  updateMaintenance,
  type Device,
  type Maintenance as TraccarMaintenance,
} from "@/lib/traccar-api";
import { deleteVehicleService, fetchVehicleServices } from "@/lib/reseller-api";
import { getLinkedDeviceId, getMaintenanceProgress, isPresetServiceType, type MaintenanceStatus } from "@/lib/maintenance-status";

type MaintenanceType = "totalDistance" | "hours" | "date";
type TypeFilter = "all" | MaintenanceType;
type StatusFilter = "all" | MaintenanceStatus;

const MAINTENANCE_TYPES: MaintenanceType[] = ["totalDistance", "hours", "date"];
const STATUSES: MaintenanceStatus[] = ["overdue", "dueSoon", "ok", "done", "unknown"];

const STATUS_BADGE: Record<MaintenanceStatus, string> = {
  overdue: "bg-red-500/15 text-red-600 dark:text-red-400 border-transparent",
  dueSoon: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-transparent",
  ok: "bg-green-500/15 text-green-700 dark:text-green-400 border-transparent",
  done: "bg-muted text-muted-foreground border-transparent",
  unknown: "bg-muted text-muted-foreground border-transparent",
};

const formatNumber = (value: number | null, digits = 0) =>
  value == null ? "-" : value.toLocaleString(undefined, { maximumFractionDigits: digits });

const toApiValue = (type: MaintenanceType, value: string) => {
  if (!value) return 0;
  if (type === "totalDistance") return Math.max(0, Number(value)) * 1000;
  if (type === "hours") return Math.max(0, Number(value)) * 3600000;
  return new Date(value).getTime();
};

const toApiPeriod = (type: MaintenanceType, value: string) => {
  if (!value) return 0;
  if (type === "date") return Math.max(0, Number(value)) * 24 * 60 * 60 * 1000;
  return toApiValue(type, value);
};

const fromApiValue = (type: string, value: number) => {
  if (!value) return "";
  if (type === "totalDistance") return String(Math.round(value / 1000));
  if (type === "hours") return String(Math.round((value / 3600000) * 10) / 10);
  if (type === "date") return new Date(value).toISOString().slice(0, 10);
  return String(value);
};

const fromApiPeriod = (type: string, value: number) => {
  if (!value) return "";
  if (type === "date") return String(Math.round(value / (24 * 60 * 60 * 1000)));
  return fromApiValue(type, value);
};

const formatValue = (type: string, value: number) => {
  if (!value) return "-";
  if (type === "totalDistance") return `${Math.round(value / 1000).toLocaleString()} km`;
  if (type === "hours") return `${(value / 3600000).toLocaleString(undefined, { maximumFractionDigits: 1 })} h`;
  if (type === "date") return new Date(value).toLocaleDateString();
  return value.toLocaleString();
};

const formatPeriod = (type: string, value: number) => {
  if (!value) return "-";
  if (type === "date") return `${Math.round(value / (24 * 60 * 60 * 1000)).toLocaleString()} d`;
  return formatValue(type, value);
};

const formatRemaining = (type: string, remaining: number) => {
  const sign = remaining < 0 ? "-" : "";
  return `${sign}${formatPeriod(type, Math.abs(remaining))}`;
};

export default function Maintenance() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const { user } = useTraccarSession();
  // Readonly users can view the service log but not add to it.
  const canLogServices = !user?.readonly;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [serviceOpen, setServiceOpen] = useState(false);
  const [servicing, setServicing] = useState<TraccarMaintenance | null>(null);
  const [historyDeviceFilter, setHistoryDeviceFilter] = useState("all");
  const [historySearch, setHistorySearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<TraccarMaintenance | null>(null);
  const [formType, setFormType] = useState<MaintenanceType>("totalDistance");

  const { data: maintenances = [], isLoading } = useQuery({
    queryKey: ["maintenances"],
    queryFn: fetchMaintenances,
  });

  const { data: devices = [] } = useQuery({
    queryKey: ["devices"],
    queryFn: fetchDevices,
  });

  const { data: positions = [] } = useQuery({
    queryKey: ["positions"],
    queryFn: fetchPositions,
  });

  const { data: services = [], isLoading: isLoadingServices } = useQuery({
    queryKey: ["vehicle-services"],
    queryFn: fetchVehicleServices,
  });

  const deviceById = useMemo(() => new Map(devices.map((device) => [device.id, device])), [devices]);
  const positionByDeviceId = useMemo(() => new Map(positions.map((position) => [position.deviceId, position])), [positions]);

  const progressById = useMemo(() => new Map(maintenances.map((maintenance) => {
    const deviceId = getLinkedDeviceId(maintenance);
    return [maintenance.id, getMaintenanceProgress(maintenance, deviceId ? positionByDeviceId.get(deviceId) : null)];
  })), [maintenances, positionByDeviceId]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return maintenances.filter((maintenance) => {
      if (typeFilter !== "all" && maintenance.type !== typeFilter) return false;
      if (statusFilter !== "all" && progressById.get(maintenance.id)?.status !== statusFilter) return false;
      const device = getLinkedDeviceId(maintenance) ? deviceById.get(getLinkedDeviceId(maintenance)!) : null;
      const haystack = [
        maintenance.name,
        maintenance.type,
        maintenance.attributes?.notes,
        device?.name,
        device?.uniqueId,
      ].filter(Boolean).join(" ").toLowerCase();
      return !query || haystack.includes(query);
    });
  }, [deviceById, maintenances, progressById, search, statusFilter, typeFilter]);

  const serviceTypeLabel = useCallback(
    (value: string) => (isPresetServiceType(value) ? t(`reseller.maintenance.serviceTypes.${value}`) : value),
    [t],
  );

  const filteredServices = useMemo(() => {
    const query = historySearch.trim().toLowerCase();
    return services.filter((service) => {
      if (historyDeviceFilter !== "all" && String(service.device_id) !== historyDeviceFilter) return false;
      const haystack = [serviceTypeLabel(service.service_type), service.workshop, service.notes, deviceById.get(service.device_id)?.name]
        .filter(Boolean).join(" ").toLowerCase();
      return !query || haystack.includes(query);
    });
  }, [deviceById, historyDeviceFilter, historySearch, serviceTypeLabel, services]);

  const countByStatus = (status: MaintenanceStatus) =>
    maintenances.filter((maintenance) => progressById.get(maintenance.id)?.status === status).length;
  const overdueCount = countByStatus("overdue");
  const dueSoonCount = countByStatus("dueSoon");
  const filteredCost = filteredServices.reduce((sum, service) => sum + (Number(service.cost) || 0), 0);

  const deleteServiceMutation = useMutation({
    mutationFn: deleteVehicleService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-services"] });
      toast({ title: t("reseller.maintenance.serviceDeleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const openService = (maintenance: TraccarMaintenance | null) => {
    setServicing(maintenance);
    setServiceOpen(true);
  };

  const toggleStatusFilter = (status: MaintenanceStatus) =>
    setStatusFilter((current) => (current === status ? "all" : status));

  const createMutation = useMutation({
    mutationFn: async (payload: { maintenance: Omit<TraccarMaintenance, "id">; deviceId: number | null }) => {
      const maintenance = await createMaintenance(payload.maintenance);
      if (payload.deviceId) await linkMaintenanceToDevice(payload.deviceId, maintenance.id);
      return maintenance;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenances"] });
      setIsOpen(false);
      toast({ title: t("reseller.maintenance.created") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { maintenance: TraccarMaintenance; deviceId: number | null; previousDeviceId: number | null }) => {
      const maintenance = await updateMaintenance(payload.maintenance);
      if (payload.previousDeviceId && payload.previousDeviceId !== payload.deviceId) {
        await unlinkMaintenanceFromDevice(payload.previousDeviceId, maintenance.id);
      }
      if (payload.deviceId && payload.previousDeviceId !== payload.deviceId) {
        await linkMaintenanceToDevice(payload.deviceId, maintenance.id);
      }
      return maintenance;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenances"] });
      setIsOpen(false);
      setEditing(null);
      toast({ title: t("reseller.maintenance.updated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMaintenance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenances"] });
      toast({ title: t("reseller.maintenance.deleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setEditing(null);
      setFormType("totalDistance");
    }
  };

  const openEdit = (maintenance: TraccarMaintenance) => {
    setEditing(maintenance);
    setFormType(MAINTENANCE_TYPES.includes(maintenance.type as MaintenanceType) ? maintenance.type as MaintenanceType : "totalDistance");
    setIsOpen(true);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const deviceIdValue = formData.get("device_id") as string;
    const deviceId = deviceIdValue && deviceIdValue !== "none" ? Number(deviceIdValue) : null;
    const type = formData.get("type") as MaintenanceType;
    const attributes: Record<string, string | number | boolean> = {
      ...(editing?.attributes ?? {}),
    };
    const notes = formData.get("notes") as string;
    if (notes) attributes.notes = notes;
    else delete attributes.notes;
    if (deviceId) attributes.deviceId = deviceId;
    else delete attributes.deviceId;

    const maintenance = {
      ...(editing ? { id: editing.id } : {}),
      name: formData.get("name") as string,
      type,
      start: toApiValue(type, formData.get("start") as string),
      period: toApiPeriod(type, formData.get("period") as string),
      attributes,
    };

    if (editing) {
      updateMutation.mutate({
        maintenance: maintenance as TraccarMaintenance,
        deviceId,
        previousDeviceId: getLinkedDeviceId(editing),
      });
    } else {
      createMutation.mutate({ maintenance: maintenance as Omit<TraccarMaintenance, "id">, deviceId });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold">{t("reseller.maintenance.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("reseller.maintenance.subtitle")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
          {canLogServices && (
          <Button variant="outline" onClick={() => openService(null)}>
            <ClipboardCheck className="h-4 w-4 mr-2" />
            {t("reseller.maintenance.registerService")}
          </Button>
          )}
          <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                {t("reseller.maintenance.create")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>{editing ? t("reseller.maintenance.edit") : t("reseller.maintenance.create")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t("devices.name")} *</Label>
                    <Input id="name" name="name" required defaultValue={editing?.name || ""} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="device_id">{t("reseller.fieldService.device")}</Label>
                    <Select name="device_id" defaultValue={editing ? String(getLinkedDeviceId(editing) ?? "none") : "none"}>
                      <SelectTrigger id="device_id">
                        <SelectValue placeholder={t("reseller.fieldService.noDevice")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("reseller.fieldService.noDevice")}</SelectItem>
                        {devices.map((device: Device) => (
                          <SelectItem key={device.id} value={String(device.id)}>
                            {device.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="type">{t("reseller.maintenance.type")}</Label>
                    <Select name="type" value={formType} onValueChange={(value) => setFormType(value as MaintenanceType)}>
                      <SelectTrigger id="type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MAINTENANCE_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>{t(`reseller.maintenance.types.${type}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="start">{t("reseller.maintenance.start")}</Label>
                    <Input
                      id="start"
                      name="start"
                      type={formType === "date" ? "date" : "number"}
                      min={formType === "date" ? undefined : "0"}
                      step={formType === "hours" ? "0.1" : "1"}
                      defaultValue={editing ? fromApiValue(formType, editing.start) : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="period">{t("reseller.maintenance.period")}</Label>
                    <Input
                      id="period"
                      name="period"
                      type="number"
                      min="0"
                      step={formType === "hours" ? "0.1" : "1"}
                      defaultValue={editing ? fromApiPeriod(formType, editing.period) : ""}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">{t("reseller.fieldService.notes")}</Label>
                  <Textarea id="notes" name="notes" defaultValue={String(editing?.attributes?.notes || "")} rows={2} />
                </div>
                <Button type="submit" className="w-full" disabled={isSaving}>
                  {editing ? t("common.update") : t("common.create")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <ServiceDialog
          key={`${servicing?.id ?? "new"}-${serviceOpen}`}
          open={serviceOpen}
          onOpenChange={setServiceOpen}
          devices={devices}
          positionByDeviceId={positionByDeviceId}
          maintenance={servicing}
        />

        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("common.total")}</p>
                <p className="text-2xl font-bold">{maintenances.length}</p>
              </div>
              <Wrench className="h-5 w-5 text-primary" />
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-colors ${statusFilter === "overdue" ? "ring-2 ring-red-500" : ""}`}
            onClick={() => toggleStatusFilter("overdue")}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("reseller.maintenance.status.overdue")}</p>
                <p className="text-2xl font-bold">{overdueCount}</p>
              </div>
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-colors ${statusFilter === "dueSoon" ? "ring-2 ring-amber-500" : ""}`}
            onClick={() => toggleStatusFilter("dueSoon")}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("reseller.maintenance.status.dueSoon")}</p>
                <p className="text-2xl font-bold">{dueSoonCount}</p>
              </div>
              <Clock className="h-5 w-5 text-amber-500" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("reseller.maintenance.servicesLogged")}</p>
                <p className="text-2xl font-bold">{services.length}</p>
              </div>
              <Receipt className="h-5 w-5 text-green-500" />
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="reminders">
          <TabsList>
            <TabsTrigger value="reminders">{t("reseller.maintenance.tabs.reminders")}</TabsTrigger>
            <TabsTrigger value="history">{t("reseller.maintenance.tabs.history")}</TabsTrigger>
          </TabsList>
          <TabsContent value="reminders">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="relative w-full md:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("reseller.maintenance.searchPlaceholder")}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex flex-col gap-3 md:flex-row">
              <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as TypeFilter)}>
                <SelectTrigger className="w-full md:w-[210px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("reseller.maintenance.allTypes")}</SelectItem>
                  {MAINTENANCE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{t(`reseller.maintenance.types.${type}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("reseller.maintenance.allStatuses")}</SelectItem>
                  {STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{t(`reseller.maintenance.status.${status}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("reseller.maintenance.noMaintenance")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("devices.name")}</TableHead>
                    <TableHead>{t("reseller.fieldService.device")}</TableHead>
                    <TableHead>{t("reseller.maintenance.type")}</TableHead>
                    <TableHead>{t("reseller.maintenance.statusLabel")}</TableHead>
                    <TableHead>{t("reseller.maintenance.current")}</TableHead>
                    <TableHead>{t("reseller.maintenance.nextDue")}</TableHead>
                    <TableHead>{t("reseller.maintenance.period")}</TableHead>
                    <TableHead>{t("reseller.fieldService.notes")}</TableHead>
                    <TableHead className="w-[140px]">{t("reseller.common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((maintenance) => {
                    const linkedDeviceId = getLinkedDeviceId(maintenance);
                    const device = linkedDeviceId ? deviceById.get(linkedDeviceId) : null;
                    const progress = progressById.get(maintenance.id);
                    const status = progress?.status ?? "unknown";
                    return (
                      <TableRow key={maintenance.id}>
                        <TableCell>{maintenance.name}</TableCell>
                        <TableCell>{device?.name || "-"}</TableCell>
                        <TableCell>{t(`reseller.maintenance.types.${maintenance.type}`, maintenance.type)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={STATUS_BADGE[status]}>
                            {t(`reseller.maintenance.status.${status}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>{progress?.current != null ? formatValue(maintenance.type, progress.current) : "-"}</TableCell>
                        <TableCell>
                          {progress?.due != null ? (
                            <div>
                              <div>{formatValue(maintenance.type, progress.due)}</div>
                              {progress.remaining != null && (
                                <div className={`text-xs ${progress.remaining < 0 ? "text-red-500" : "text-muted-foreground"}`}>
                                  {formatRemaining(maintenance.type, progress.remaining)}
                                </div>
                              )}
                            </div>
                          ) : formatValue(maintenance.type, maintenance.start)}
                        </TableCell>
                        <TableCell>{formatPeriod(maintenance.type, maintenance.period)}</TableCell>
                        <TableCell className="max-w-[260px] truncate">{String(maintenance.attributes?.notes || "-")}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {canLogServices && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={t("reseller.maintenance.registerService")}
                              disabled={!linkedDeviceId}
                              onClick={() => openService(maintenance)}
                            >
                              <ClipboardCheck className="h-4 w-4" />
                            </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => openEdit(maintenance)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                if (confirm(t("reseller.common.deleteConfirm"))) deleteMutation.mutate(maintenance.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="relative w-full md:max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t("reseller.maintenance.searchServices")}
                      value={historySearch}
                      onChange={(event) => setHistorySearch(event.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <span className="text-sm text-muted-foreground">
                      {t("reseller.maintenance.totalCost")}: <span className="font-semibold text-foreground">{formatNumber(filteredCost, 2)}</span>
                    </span>
                    <Select value={historyDeviceFilter} onValueChange={setHistoryDeviceFilter}>
                      <SelectTrigger className="w-full md:w-[220px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("reseller.maintenance.allVehicles")}</SelectItem>
                        {devices.map((device) => (
                          <SelectItem key={device.id} value={String(device.id)}>{device.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingServices ? (
                  <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
                ) : filteredServices.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">{t("reseller.maintenance.noServices")}</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("reseller.maintenance.performedAt")}</TableHead>
                        <TableHead>{t("reseller.fieldService.vehicle")}</TableHead>
                        <TableHead>{t("reseller.fieldService.serviceType")}</TableHead>
                        <TableHead>{t("reseller.maintenance.odometer")}</TableHead>
                        <TableHead>{t("reseller.maintenance.engineHours")}</TableHead>
                        <TableHead>{t("reseller.maintenance.cost")}</TableHead>
                        <TableHead>{t("reseller.maintenance.workshop")}</TableHead>
                        <TableHead>{t("reseller.fieldService.notes")}</TableHead>
                        <TableHead className="w-[60px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredServices.map((service) => (
                        <TableRow key={service.id}>
                          <TableCell>{new Date(`${service.performed_at}T00:00:00`).toLocaleDateString()}</TableCell>
                          <TableCell>{deviceById.get(service.device_id)?.name || `#${service.device_id}`}</TableCell>
                          <TableCell>{serviceTypeLabel(service.service_type)}</TableCell>
                          <TableCell>{service.odometer_km != null ? `${formatNumber(Number(service.odometer_km))} km` : "-"}</TableCell>
                          <TableCell>{service.engine_hours != null ? `${formatNumber(Number(service.engine_hours), 1)} h` : "-"}</TableCell>
                          <TableCell>{formatNumber(service.cost != null ? Number(service.cost) : null, 2)}</TableCell>
                          <TableCell>{service.workshop || "-"}</TableCell>
                          <TableCell className="max-w-[220px] truncate">{service.notes || "-"}</TableCell>
                          <TableCell>
                            {canLogServices && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                if (confirm(t("reseller.common.deleteConfirm"))) deleteServiceMutation.mutate(service.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
